import { Database, Loader2, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';

const DatasetSidebar = ({
  sources,
  previewDataset,
  setPreviewDataset,
  onConnect,
  onRemoveSource,
  onRebuildSource,
  removingSourceId = null
}) => {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-1.5 space-y-px">
        {sources.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg mb-2">
              <Database size={16} className="text-gray-300 dark:text-gray-700" />
            </div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-600 uppercase tracking-wider">
              No datasets
            </p>
          </div>
        ) : (
          sources.map(source => {
            const isBusy =
              source.status === 'loading' || source.status === 'generating';
            const isRemoving = removingSourceId === source.id;
            return (
            <div
              key={source.id}
              onClick={() => setPreviewDataset(source)}
              title={`${source.name}${source.type ? ` · ${source.type}` : ''}`}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-md transition-all cursor-pointer group relative",
                previewDataset?.id === source.id
                  ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300"
                  : "hover:bg-gray-50 dark:hover:bg-gray-900/30 text-gray-700 dark:text-gray-300"
              )}
            >
              <div className={cn(
                "shrink-0 transition-colors",
                previewDataset?.id === source.id
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-400"
              )}>
                <Database size={13} />
              </div>
              <div className="flex-1 min-w-0 pr-5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <h4 className={cn(
                    "text-xs truncate leading-tight",
                    previewDataset?.id === source.id
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-gray-900 dark:text-gray-100"
                  )}>
                    {source.name}
                  </h4>
                  {source.status && source.status !== 'ready' && (
                    <span className={cn(
                      "text-[8px] font-medium uppercase tracking-wider px-1 py-0.5 rounded shrink-0",
                      source.status === 'failed'
                        ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                        : "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 animate-pulse"
                    )}>
                      {source.status === 'loading' ? 'Loading' :
                        source.status === 'generating' ? 'Generating' :
                          source.status}
                    </span>
                  )}
                </div>
                {source.status === 'failed' && (
                  <p
                    className="mt-0.5 text-[9px] font-medium leading-snug text-red-600 dark:text-red-400 line-clamp-2"
                    title={(source.summary && String(source.summary).trim()) || undefined}
                  >
                    {(source.summary && String(source.summary).trim()) || 'No error message was recorded.'}
                  </p>
                )}
              </div>
              <button
                type="button"
                disabled={isBusy || isRemoving}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isBusy && !isRemoving) onRemoveSource(source.id);
                }}
                className={cn(
                  "absolute right-1 top-1/2 -translate-y-1/2 p-0.5 transition-all rounded",
                  isRemoving || previewDataset?.id === source.id
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100",
                  isBusy
                    ? "text-gray-200 dark:text-gray-800 cursor-not-allowed"
                    : isRemoving
                      ? "text-gray-500 dark:text-gray-400 cursor-wait"
                      : "text-gray-300 hover:text-red-500 dark:text-gray-700 dark:hover:text-red-400"
                )}
                title={
                  isRemoving
                    ? 'Removing…'
                    : isBusy
                      ? 'Unavailable while dataset is loading'
                      : 'Remove dataset'
                }
              >
                {isRemoving ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Trash2 size={11} />
                )}
              </button>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DatasetSidebar;
